import React, { useState, useEffect } from 'react';
import API_CONFIG from '../utils/apiConfig';
import './ActionLogsTab.css';
import '../styles/pagination-mobile.css';

const ActionLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchLogs();
  }, [actionFilter, entityTypeFilter, page, limit]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      if (actionFilter) params.append('action', actionFilter);
      if (entityTypeFilter) params.append('entity_type', entityTypeFilter);

      const url = API_CONFIG.getApiUrl(`/action-logs/logs?${params.toString()}`);
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setTotalCount(data.total_count || 0);
        setTotalPages(Math.ceil((data.total_count || 0) / limit));
      } else {
        if (window.showToast) {
          window.showToast('Failed to fetch action logs', 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching action logs:', error);
      if (window.showToast) {
        window.showToast('Error fetching action logs', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogDetail = async (logId) => {
    try {
      const url = API_CONFIG.getApiUrl(`/action-logs/logs/${logId}`);
      const response = await API_CONFIG.fetchWithAuth(url, { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        setSelectedLog(data);
        setShowDetailModal(true);
      } else {
        if (window.showToast) {
          window.showToast('Failed to fetch log detail', 'error');
        }
      }
    } catch (error) {
      console.error('Error fetching log detail:', error);
      if (window.showToast) {
        window.showToast('Error fetching log detail', 'error');
      }
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    try {
      return new Date(isoString).toLocaleString();
    } catch (e) {
      return isoString;
    }
  };

  const formatAction = (action) => {
    if (!action) return '-';
    return action.replace(/_/g, ' ');
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
  };

  return (
    <div className="action-logs-container">
      <div className="action-logs-header">
        <h2>User Action Logs</h2>
        <button onClick={fetchLogs} className="refresh-btn">🔄 Refresh</button>
      </div>

      <div className="action-logs-filters">
        <div className="filter-group">
          <label>Action:</label>
          <select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}>
            <option value="">All Actions</option>
            <option value="login">login</option>
            <option value="register">register</option>
            <option value="verify_email">verify_email</option>
            <option value="complete_profile">complete_profile</option>
            <option value="switch_role">switch_role</option>
            <option value="forgot_password">forgot_password</option>
            <option value="reset_password">reset_password</option>
            <option value="invite_subcontractor">invite_subcontractor</option>
            <option value="accept_invite">accept_invite</option>
            <option value="send_connection_request">send_connection_request</option>
            <option value="accept_connection_request">accept_connection_request</option>
            <option value="reject_connection_request">reject_connection_request</option>
            <option value="remove_relationship">remove_relationship</option>
            <option value="create_payment">create_payment</option>
            <option value="edit_payment">edit_payment</option>
            <option value="approve_payment">approve_payment</option>
            <option value="reject_payment">reject_payment</option>
            <option value="mark_payment_paid">mark_payment_paid</option>
            <option value="withdraw_payment">withdraw_payment</option>
            <option value="bulk_complete_payments">bulk_complete_payments</option>
            <option value="create_employer_user">create_employer_user</option>
            <option value="update_employer_user">update_employer_user</option>
            <option value="delete_employer_user">delete_employer_user</option>
            <option value="approve_company">approve_company</option>
            <option value="reject_company">reject_company</option>
            <option value="submit_cis_hmrc">submit_cis_hmrc</option>
            <option value="verify_subcontractor_hmrc">verify_subcontractor_hmrc</option>
            <option value="become_employer">become_employer</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Entity Type:</label>
          <select value={entityTypeFilter} onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option value="payment_request">payment_request</option>
            <option value="user">user</option>
            <option value="relationship">relationship</option>
            <option value="invite">invite</option>
            <option value="hmrc_submission">hmrc_submission</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading action logs...</div>
      ) : logs.length === 0 ? (
        <div className="empty-state">No action logs found</div>
      ) : (
        <div className="action-logs-table-container">
          <table className="action-logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Action</th>
                <th>Entity</th>
                <th>IP</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.log_id}>
                  <td>{formatDateTime(log.created_at)}</td>
                  <td>{log.user_email || log.user_id}</td>
                  <td><span className="action-badge">{formatAction(log.action)}</span></td>
                  <td>{log.entity_type && log.entity_id ? `${log.entity_type} (${log.entity_id?.slice(0, 8)}…)` : '-'}</td>
                  <td>{log.ip_address || '-'}</td>
                  <td>
                    <button
                      className="view-detail-btn"
                      onClick={() => fetchLogDetail(log.log_id)}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} results
          </div>
          <div className="pagination-controls">
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="pagination-select"
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="pagination-button"
            >
              Previous
            </button>
            <span className="pagination-page-info">
              Page {page} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showDetailModal && selectedLog && (
        <div className="action-detail-modal-overlay" onClick={closeDetailModal}>
          <div className="action-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Action Log Detail</h3>
              <button className="close-btn" onClick={closeDetailModal}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>User:</strong> {selectedLog.user_email || selectedLog.user_id}</p>
              <p><strong>Action:</strong> {formatAction(selectedLog.action)}</p>
              <p><strong>Entity:</strong> {selectedLog.entity_type} / {selectedLog.entity_id || '-'}</p>
              <p><strong>IP:</strong> {selectedLog.ip_address || '-'}</p>
              <p><strong>Time:</strong> {formatDateTime(selectedLog.created_at)}</p>
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <>
                  <p><strong>Details:</strong></p>
                  <pre className="details-json">{JSON.stringify(selectedLog.details, null, 2)}</pre>
                </>
              )}
              {selectedLog.user_agent && (
                <p><strong>User Agent:</strong> <small>{selectedLog.user_agent}</small></p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActionLogsTab;
